import axios from 'axios';

/**
 * Deployment-specific branding. Every field defaults to an empty string, which
 * means "no customer branding" — that is what dev and staging run with.
 *
 * A customer VM overrides these by mounting its own file over
 * `/ragflow/web/dist/conf.json` (see deploy/DEPLOY.md), so the same image can
 * be promoted from dev to staging to any customer without a code change.
 */
export interface BrandConf {
  /** Replaces the localized login headline (`login.title`). */
  loginTitle: string;
  /** Public path of a customer logo placed next to the optimAIse wordmark. */
  coBrandLogo: string;
  /** Accessible name for `coBrandLogo`. */
  coBrandName: string;
}

export interface AppConf {
  appName: string;
  brand: BrandConf;
}

const defaultAppConf: AppConf = {
  appName: 'optimAIse RAG',
  brand: {
    loginTitle: '',
    coBrandLogo: '',
    coBrandName: '',
  },
};

let appConf: AppConf = defaultAppConf;

const isObject = (value: unknown) =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Fetched once from `app.tsx`'s `render` hook, before the first paint — a
 * per-component fetch would let the neutral wordmark flash before the
 * customer logo appears.
 */
export const loadAppConf = async () => {
  try {
    const { data } = await axios.get<Partial<AppConf>>('/conf.json');

    // A dev server or a misconfigured nginx can answer with the SPA's
    // index.html instead of a 404, so only merge an actual object.
    if (isObject(data)) {
      appConf = {
        ...defaultAppConf,
        ...data,
        brand: {
          ...defaultAppConf.brand,
          ...(isObject(data.brand) ? data.brand : undefined),
        },
      };
    }
  } catch (error) {
    console.warn('Could not load /conf.json, keeping default branding', error);
  }

  return appConf;
};

export const getAppConf = () => appConf;

export const getBrandConf = () => appConf.brand;
